/**
 * Data Quality grid — rows × months with mini-bar columns, issue dots, gap markers, and a status column.
 *
 * Visual contract (per design):
 *   • Each (row × month) cell renders a "chart column" — a bar whose height
 *     scales to the row max, capped with a strong horizontal line so the cells
 *     read like a chart rather than a heat-map. Columns sit close together
 *     (tight horizontal gap) so a row reads as a single sparkline.
 *   • A coloured dot above the cap signals a fired check (orange = warning,
 *     red = critical). Most-severe wins when several rules fire on one cell.
 *   • Gaps (no activity for that row × month) render as a borderless empty
 *     slot with a small red `×` — never as a striped or dashed cell.
 *   • Hovering a column reveals an info card with the month's CO₂e, activity
 *     count, and any detected issues.
 *   • The card never produces a horizontal scrollbar — the table uses fixed
 *     layout so column widths flex with the available space.
 *
 * The header exposes a `headerActions` slot (e.g. a "Settings" button that
 * opens the detection-thresholds modal) so the controls live one click away
 * without adding a permanent panel above the table.
 */

import { type ReactNode } from 'react';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@g17eco/ui-modern/components/ui/card';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { type LucideIcon, X } from 'lucide-react';
import {
  type CellIssue,
  type IssueSeverity,
  peakSeverity,
} from './dataQualityDetection';

export interface DataQualityRowDef {
  /** Stable key per source-row, e.g. `scope1__stationary_combustion__natural_gas`. */
  key: string;
  /** Headline label rendered as the row's primary title (e.g. "Natural Gas"). */
  title: string;
  /** Optional supporting label rendered under the title (e.g. "Stationary Combustion"). */
  subtitle?: string;
  /** Lucide icon to mirror the activity-type branding on the left edge. */
  Icon: LucideIcon;
  iconColor: string;
  scopeLabel: string;
  scopeBadgeColor: string;
  /** Per-month CO₂e values; `null` ⇒ no activity recorded for that month. */
  values: ReadonlyArray<number | null>;
  /** Per-month activity counts (parallel to `values`). Used in the cell hover card. */
  counts: ReadonlyArray<number>;
  /**
   * Per-month list of activity IDs that contributed to the cell. Empty array
   * for clean / gap cells. Drives the cell-click → activity drawer / picker.
   */
  activityIdsByBucket: ReadonlyArray<ReadonlyArray<string>>;
  /** Per-month detected issues. Length matches `values`. */
  issues: ReadonlyArray<ReadonlyArray<CellIssue>>;
}

interface DataQualityGridProps {
  rows: ReadonlyArray<DataQualityRowDef>;
  monthLabels: ReadonlyArray<string>;
  /** When false, gaps render as a plain empty cell rather than a red cross. */
  gapDetectionEnabled: boolean;
  /** Optional slot rendered in the card header (e.g. a Settings button). */
  headerActions?: ReactNode;
  /**
   * When provided, gap cells become clickable buttons. The handler receives
   * the row key and month index so the parent can build a prefilled
   * "Add Activity" payload (scope, activityType, subType, bucket bounds).
   */
  onGapClick?: (rowKey: string, monthIndex: number) => void;
  /**
   * When provided, non-gap cells with at least one activity become clickable.
   * The handler receives the row key + month index so the parent can resolve
   * the underlying activity IDs and open the calculation drawer (single
   * activity) or a picker modal (multiple activities in the bucket).
   */
  onCellClick?: (rowKey: string, monthIndex: number) => void;
}

const SEVERITY_DOT: Record<IssueSeverity, string> = {
  warning: 'tw:bg-amber-500',
  critical: 'tw:bg-red-500',
};

const SEVERITY_LABEL: Record<IssueSeverity, string> = {
  warning: 'warning',
  critical: 'critical',
};

const formatCo2e = (kg: number): string => {
  if (kg === 0) return '0 kg';
  if (kg >= 1000) return `${(kg / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })} t`;
  return `${kg.toLocaleString(undefined, { maximumFractionDigits: 1 })} kg`;
};

const countRowIssues = (issues: ReadonlyArray<ReadonlyArray<CellIssue>>): { critical: number; warning: number } => {
  let critical = 0;
  let warning = 0;
  for (const cellIssues of issues) {
    const peak = peakSeverity(cellIssues);
    if (peak === 'critical') critical++;
    else if (peak === 'warning') warning++;
  }
  return { critical, warning };
};

const countRowGaps = (values: ReadonlyArray<number | null>): number =>
  values.reduce<number>((sum, v) => sum + (v === null ? 1 : 0), 0);

export const DataQualityGrid = ({ rows, monthLabels, gapDetectionEnabled, headerActions, onGapClick, onCellClick }: DataQualityGridProps) => {
  if (rows.length === 0) {
    return (
      <Card>
        <CardContent>
          <p className='tw:text-sm tw:text-muted-foreground tw:text-center tw:py-8 tw:mb-0'>
            No source data for this asset in the selected period.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className='tw:flex tw:items-center tw:justify-between tw:gap-3'>
          <CardTitle className='tw:text-base'>Data quality grid</CardTitle>
          {headerActions}
        </div>
      </CardHeader>
      <CardContent className='tw:p-0'>
        {/*
          Long all-time spans pack many columns into the row, so we degrade
          the column header gracefully:
            • ≤ 24 buckets: full label per column ("Jan '25").
            • > 24 buckets: only year-boundary columns show a label ("'25"),
              everything else is blank — the chart columns can shrink to a
              few pixels without label collisions, and the year ticks still
              tell the user where they are on the timeline.
          Past ~60 columns the chart needs more than the card width to stay
          readable, so we let the table establish a sensible min-width and
          let the card scroll horizontally as a last resort.
        */}
        <div className={monthLabels.length > 60 ? 'tw:overflow-x-auto' : ''}>
          <table
            className='tw:w-full tw:text-sm tw:border-collapse tw:table-fixed'
            style={{ minWidth: monthLabels.length > 60 ? `${370 + monthLabels.length * 8}px` : undefined }}
          >
            <colgroup>
              <col style={{ width: '250px' }} />
              {monthLabels.map((label) => (
                <col key={label} style={{ width: `${100 / Math.max(1, monthLabels.length)}%` }} />
              ))}
              <col style={{ width: '120px' }} />
            </colgroup>
            <thead>
              <tr className='tw:text-xs tw:text-muted-foreground tw:border-b tw:border-border'>
                <th className='tw:text-left tw:pl-6 tw:pr-3 tw:py-2 tw:font-medium'>Source</th>
                {monthLabels.length <= 24 ? (
                  monthLabels.map((label, i) => (
                    <th key={`${label}-${i}`} className='tw:text-center tw:py-2 tw:font-medium tw:overflow-hidden'>
                      {label}
                    </th>
                  ))
                ) : (
                  // Long span: collapse the per-column labels into a single
                  // colSpan'd cell with a 3-zone flex showing first / mid /
                  // last bucket labels. Per-column widths still come from
                  // the <colgroup>, so body cells stay equal-width — only
                  // the header changes shape.
                  <th
                    colSpan={monthLabels.length}
                    className='tw:py-2 tw:font-medium tw:px-2'
                  >
                    <div className='tw:flex tw:items-center tw:justify-between tw:text-muted-foreground'>
                      <span>{monthLabels[0]}</span>
                      <span>{monthLabels[Math.floor(monthLabels.length / 2)]}</span>
                      <span>{monthLabels[monthLabels.length - 1]}</span>
                    </div>
                  </th>
                )}
                <th className='tw:text-right tw:px-4 tw:py-2 tw:font-medium'>Status</th>
              </tr>
            </thead>
          <tbody>
            {rows.map((row) => {
              const max = Math.max(0, ...row.values.map((v) => v ?? 0));
              const { critical, warning } = countRowIssues(row.issues);
              const gaps = gapDetectionEnabled ? countRowGaps(row.values) : 0;
              const totalIssues = critical + warning + gaps;
              return (
                <tr key={row.key} className='tw:border-b tw:border-border tw:hover:bg-muted/30'>
                  <td className='tw:pl-6 tw:pr-3 tw:py-3'>
                    <div className='tw:flex tw:items-center tw:gap-2'>
                      <row.Icon className={cn('tw:size-4 tw:shrink-0', row.iconColor)} />
                      <div className='tw:min-w-0'>
                        <div className='tw:text-sm tw:font-medium tw:truncate'>{row.title}</div>
                        <div className='tw:flex tw:items-center tw:gap-1.5 tw:mt-0.5'>
                          <Badge
                            variant='outline'
                            className={cn('tw:text-[10px] tw:font-normal', row.scopeBadgeColor)}
                          >
                            {row.scopeLabel}
                          </Badge>
                          {row.subtitle && (
                            <span className='tw:text-[11px] tw:text-muted-foreground tw:truncate'>{row.subtitle}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  {row.values.map((value, i) => {
                    const cellIssues = row.issues[i] ?? [];
                    const peak = peakSeverity(cellIssues);
                    const isGap = value === null;
                    const ids = row.activityIdsByBucket[i] ?? [];
                    const dataClick = onCellClick && !isGap && ids.length > 0
                      ? () => onCellClick(row.key, i)
                      : undefined;
                    return (
                      <td key={i} className='tw:px-px tw:py-3 tw:align-bottom'>
                        <DataQualityCell
                          monthLabel={monthLabels[i] ?? ''}
                          value={value}
                          count={row.counts[i] ?? 0}
                          max={max}
                          severity={peak}
                          issues={cellIssues}
                          isGap={isGap}
                          gapDetectionEnabled={gapDetectionEnabled}
                          onGapClick={onGapClick && isGap ? () => onGapClick(row.key, i) : undefined}
                          onDataClick={dataClick}
                        />
                      </td>
                    );
                  })}
                  <td className='tw:px-4 tw:py-3 tw:text-right tw:text-xs tw:text-nowrap'>
                    <RowStatus critical={critical} warning={warning} gaps={gaps} totalIssues={totalIssues} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </CardContent>
    </Card>
  );
};

interface DataQualityCellProps {
  monthLabel: string;
  value: number | null;
  count: number;
  max: number;
  severity: IssueSeverity | undefined;
  issues: ReadonlyArray<CellIssue>;
  isGap: boolean;
  gapDetectionEnabled: boolean;
  /** When provided + cell is a gap, the cell becomes a button that opens the prefilled "Add Activity" modal. */
  onGapClick?: () => void;
  /** When provided + cell has data, the bar becomes a button that opens the activity drawer / picker. */
  onDataClick?: () => void;
}

const CELL_HEIGHT_PX = 44;

const DataQualityCell = ({
  monthLabel,
  value,
  count,
  max,
  severity,
  issues,
  isGap,
  gapDetectionEnabled,
  onGapClick,
  onDataClick,
}: DataQualityCellProps) => {
  // Always wrap the cell in a `group` so the popover can show on hover. The
  // popover sits absolutely positioned above the cell and is gated by group-
  // hover so we don't pay for one DOM node per cell when the user isn't
  // hovering — Tailwind's `group-hover` is purely CSS and ~free.
  return (
    <div
      className='tw:group tw:relative tw:flex tw:flex-col tw:items-stretch tw:justify-end tw:gap-0.5'
      style={{ height: CELL_HEIGHT_PX }}
    >
      {/* dot ABOVE the bar; reserve a fixed-height row even when clean so
          chart caps line up across the table. */}
      <div className='tw:h-1.5 tw:flex tw:items-center tw:justify-center'>
        {severity && (
          <span
            className={cn('tw:size-1.5 tw:rounded-full', SEVERITY_DOT[severity])}
            aria-label={`${SEVERITY_LABEL[severity]} issue`}
          />
        )}
      </div>

      {isGap ? (
        onGapClick ? (
          // Clickable gap — keyboard-accessible button that opens the prefilled
          // "Add Activity" modal for this row × month.
          <button
            type='button'
            onClick={onGapClick}
            aria-label={`Add activity for ${monthLabel}`}
            className='tw:flex tw:items-center tw:justify-center tw:rounded tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer tw:hover:bg-red-50 tw:hover:ring-1 tw:hover:ring-red-300 tw:focus-visible:outline-none tw:focus-visible:ring-1 tw:focus-visible:ring-red-400'
            style={{ height: CELL_HEIGHT_PX - 8 }}
          >
            {gapDetectionEnabled && <X className='tw:size-4 tw:text-red-600' />}
          </button>
        ) : (
          <div className='tw:flex tw:items-center tw:justify-center' style={{ height: CELL_HEIGHT_PX - 8 }}>
            {gapDetectionEnabled && <X className='tw:size-4 tw:text-red-600' />}
          </div>
        )
      ) : onDataClick ? (
        // Clickable data cell — keyboard-accessible button wrapping the bar
        // chart column. The explicit height matches the non-clickable cell
        // so the button gives `BarColumn` a definite parent height for its
        // % heights to resolve against (a flex chain alone collapses here).
        <button
          type='button'
          onClick={onDataClick}
          aria-label={`View activities for ${monthLabel}`}
          className='tw:flex tw:items-end tw:rounded tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer tw:hover:ring-1 tw:hover:ring-emerald-400 tw:focus-visible:outline-none tw:focus-visible:ring-1 tw:focus-visible:ring-emerald-500'
          style={{ height: CELL_HEIGHT_PX - 8 }}
        >
          <BarColumn value={value ?? 0} max={max} severity={severity} />
        </button>
      ) : (
        <BarColumn value={value ?? 0} max={max} severity={severity} />
      )}

      <CellHoverCard
        monthLabel={monthLabel}
        value={value}
        count={count}
        issues={issues}
        isGap={isGap}
        clickableGap={Boolean(onGapClick)}
        clickableData={Boolean(onDataClick)}
      />
    </div>
  );
};

interface BarColumnProps {
  value: number;
  max: number;
  severity: IssueSeverity | undefined;
}

/**
 * Per-severity bar palette. Clean cells use emerald (the canonical "all good"
 * tone elsewhere on the dashboard); flagged cells take the matching dot
 * colour so a glance at the column tells you the severity without checking
 * the dot above it.
 */
const BAR_FILL: Record<'clean' | IssueSeverity, string> = {
  clean: 'tw:bg-emerald-200/60',
  warning: 'tw:bg-amber-200/70',
  critical: 'tw:bg-red-200/70',
};

const BAR_CAP: Record<'clean' | IssueSeverity, string> = {
  clean: 'tw:bg-emerald-600',
  warning: 'tw:bg-amber-600',
  critical: 'tw:bg-red-600',
};

/**
 * Single chart column — a soft fill that fades to its top edge, with a
 * **strong horizontal cap line** so the column reads like the head of a
 * bar chart rather than a heat-map cell. The bar's colour mirrors the
 * issue dot above it so severity reads at a glance.
 */
const BarColumn = ({ value, max, severity }: BarColumnProps) => {
  const heightPct = max > 0 ? Math.max(6, (value / max) * 100) : 6;
  const palette = severity ?? 'clean';
  return (
    <div className='tw:relative tw:flex-1 tw:flex tw:h-full tw:items-end'>
      <div className='tw:relative tw:w-full' style={{ height: `${heightPct}%` }}>
        <div className={cn('tw:absolute tw:inset-0 ', BAR_FILL[palette])} />
        {/* Strong cap line — same hue as the fill but darker, so even tiny
            bars have a clearly visible top edge. */}
        <div className={cn('tw:absolute tw:left-0 tw:right-0 tw:top-0 tw:h-px', BAR_CAP[palette])} />
      </div>
    </div>
  );
};

interface CellHoverCardProps {
  monthLabel: string;
  value: number | null;
  count: number;
  issues: ReadonlyArray<CellIssue>;
  isGap: boolean;
  /** When true, the gap cell is interactive — show an affordance hint in the tooltip. */
  clickableGap?: boolean;
  /** When true, the data cell is interactive — show a "Click to view" hint in the tooltip. */
  clickableData?: boolean;
}

/**
 * On-hover info card — month, CO₂e, activity count, and any fired rules.
 * Positioned above the cell via `group-hover`. Pointer-events-none so the
 * popover never steals hover from neighbouring cells.
 */
const CellHoverCard = ({ monthLabel, value, count, issues, isGap, clickableGap, clickableData }: CellHoverCardProps) => (
  <div
    className='tw:pointer-events-none tw:absolute tw:bottom-full tw:left-1/2 tw:-translate-x-1/2 tw:mb-1.5 tw:z-20 tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity tw:rounded-md tw:border tw:border-border tw:bg-popover tw:text-popover-foreground tw:shadow-md tw:px-2.5 tw:py-1.5 tw:text-xs tw:whitespace-nowrap'
    role='tooltip'
  >
    <div className='tw:font-semibold'>{monthLabel}</div>
    {isGap ? (
      <>
        <div className='tw:text-muted-foreground'>No activity recorded</div>
        {clickableGap && (
          <div className='tw:text-emerald-600 tw:font-medium tw:mt-0.5'>Click to add activity</div>
        )}
      </>
    ) : (
      <>
        <div>
          <span className='tw:text-muted-foreground'>CO₂e:</span>{' '}
          <span className='tw:font-medium'>{formatCo2e(value ?? 0)}</span>
        </div>
        <div>
          <span className='tw:text-muted-foreground'>Activities:</span>{' '}
          <span className='tw:font-medium'>{count}</span>
        </div>
        {issues.length > 0 && (
          <ul className='tw:list-none tw:m-0 tw:p-0 tw:mt-1 tw:space-y-0.5'>
            {issues.map((issue, i) => (
              <li key={i} className='tw:flex tw:items-center tw:gap-1.5'>
                <span className={cn('tw:size-1.5 tw:rounded-full', SEVERITY_DOT[issue.severity])} />
                <span>
                  <span className='tw:capitalize tw:font-medium'>{issue.severity}</span>
                  <span className='tw:text-muted-foreground'> · {issue.message}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
        {clickableData && (
          <div className='tw:text-emerald-600 tw:font-medium tw:mt-0.5'>Click to edit activity</div>
        )}
      </>
    )}
  </div>
);

interface RowStatusProps {
  critical: number;
  warning: number;
  gaps: number;
  totalIssues: number;
}

const RowStatus = ({ critical, warning, gaps, totalIssues }: RowStatusProps) => {
  if (totalIssues === 0) {
    return <span className='tw:text-emerald-600 tw:font-medium'>✓ 100%</span>;
  }
  const parts: string[] = [];
  if (critical > 0) parts.push(`${critical} critical`);
  if (warning > 0) parts.push(`${warning} warning`);
  if (gaps > 0) parts.push(`${gaps} gap${gaps === 1 ? '' : 's'}`);
  const colour = critical > 0 ? 'tw:text-red-600' : warning > 0 ? 'tw:text-amber-600' : 'tw:text-red-600';
  return (
    <span className={cn(colour, 'tw:font-medium')}>
      {parts.map((part) => (
        <div key={part}>{part}</div>
      ))}
    </span>
  );
};
